import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Camera, UserPlus, LogIn } from 'lucide-react';
import axios from 'axios';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BrowseGate = ({ children }) => {
  const { user, fingerprintReady } = useAuth();
  const location = useLocation();
  const ungatedPages = ['/login', '/register', '/verify-email', '/forgot-password', '/reset-password', '/privacy-policy', '/privacy', '/about', '/contact'];
  const onUngatedPage = ungatedPages.includes(location.pathname.replace(/\/$/, '')) || /^\/(photos|collections)\/[^/]+\/?$/.test(location.pathname);
  const [showGate, setShowGate] = useState(false);
  const pollRef = useRef(null);

  const checkSession = useCallback(async () => {
    if (user) return;
    if (onUngatedPage) return;
    if (!fingerprintReady) return;

    try {
      const fp = axios.defaults.headers.common['x-fingerprint'] || '';
      const { data } = await axios.post(`${API}/guest/session`, { fingerprint: fp });
      if (!data.enabled) return;
      if (data.seconds_remaining <= 0) {
        setShowGate(true);
        clearInterval(pollRef.current);
      }
    } catch {
      // Don't block on error
    }
  }, [user, onUngatedPage, fingerprintReady]);

  useEffect(() => {
    if (user) {
      setShowGate(false);
      clearInterval(pollRef.current);
      return;
    }

    checkSession();
    pollRef.current = setInterval(checkSession, 10000);
    return () => clearInterval(pollRef.current);
  }, [user, checkSession]);

  return (
    <>
      {children}
      {showGate && !onUngatedPage && (
        <Dialog open onOpenChange={() => {}}>
          <DialogContent aria-describedby="browse-gate-description" className="max-w-md p-6 sm:p-10 text-center [&>button]:hidden" onEscapeKeyDown={(e) => e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()}>
          <div>
            <div className="mx-auto w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-6">
              <Camera className="w-8 h-8 text-foreground/60" />
            </div>

            <DialogTitle asChild><h2
              className="text-4xl font-light mb-3"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Enjoying the gallery?
            </h2></DialogTitle>
            <DialogDescription id="browse-gate-description" className="text-muted-foreground mb-8 leading-relaxed">
              Create a free account to keep browsing and like your favourite photos.
            </DialogDescription>

            <div className="space-y-3">
              <Button asChild className="w-full h-12 rounded-xl smooth-btn text-base"><Link to="/register" className="block">
                  <UserPlus className="w-5 h-5 mr-2" />
                  Create Free Account
                </Link></Button>
              <Button asChild variant="outline" className="w-full h-12 rounded-xl text-base"><Link to="/login" className="block">
                  <LogIn className="w-5 h-5 mr-2" />
                  Sign In
                </Link></Button>
            </div>
          </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default BrowseGate;
